import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import type { EventPublicInfo, EventSponsor } from '@/types';

let cachedEvent: EventPublicInfo | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function useActiveEvent() {
  const [event, setEvent] = useState<EventPublicInfo | null>(cachedEvent);
  const [loading, setLoading] = useState(!cachedEvent);

  useEffect(() => {
    const now = Date.now();
    if (cachedEvent && now - cacheTimestamp < CACHE_TTL) {
      setEvent(cachedEvent);
      setLoading(false);
      return;
    }

    api.get<{ success: boolean; data: EventPublicInfo }>('/events/active/public')
      .then((res) => {
        cachedEvent = res.data;
        cacheTimestamp = Date.now();
        setEvent(res.data);
      })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, []);

  return { event, loading };
}

/**
 * Fetch active event WITH sponsor data (authenticated endpoint).
 * Use this on the dashboard for ad popups — keeps Base64 images off public routes.
 */
let cachedEventWithSponsors: EventPublicInfo | null = null;
let sponsorCacheTimestamp = 0;

export function useActiveEventWithSponsors() {
  const [event, setEvent] = useState<EventPublicInfo | null>(cachedEventWithSponsors);
  const [loading, setLoading] = useState(!cachedEventWithSponsors);

  useEffect(() => {
    const now = Date.now();
    if (cachedEventWithSponsors && now - sponsorCacheTimestamp < CACHE_TTL) {
      setEvent(cachedEventWithSponsors);
      setLoading(false);
      return;
    }

    api.get<{ success: boolean; data: EventPublicInfo }>('/events/active/with-sponsors')
      .then((res) => {
        cachedEventWithSponsors = res.data;
        sponsorCacheTimestamp = Date.now();
        setEvent(res.data);
      })
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, []);

  return { event, loading };
}

/**
 * Format event date range for display.
 * e.g., "January 28 – 30, 2026" or "January 28 – February 2, 2026"
 */
export function formatEventDateRange(dateStart: string, dateEnd: string): string {
  const start = new Date(dateStart);
  const end = new Date(dateEnd);

  const startMonth = start.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
  const startDay = start.getUTCDate();
  const endDay = end.getUTCDate();
  const year = end.getUTCFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} – ${endDay}, ${year}`;
  }
  return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
}

/**
 * Find the sponsor whose ad is active today (has adImageUrl and today falls
 * within adStartDate–adEndDate inclusive). Returns the first match, or null.
 */
export function getTodaysAdSponsor(sponsors?: EventSponsor[]): EventSponsor | null {
  if (!sponsors?.length) return null;

  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  for (const sponsor of sponsors) {
    if (!sponsor.adImageUrl) continue;
    if (!sponsor.adStartDate || !sponsor.adEndDate) continue;
    if (today >= sponsor.adStartDate && today <= sponsor.adEndDate) {
      return sponsor;
    }
  }
  return null;
}
